import Page from "../../layout/Page";
import PageContent from "../../layout/PageContent";
import PageContentTabs, {PageTab} from "../../layout/PageContentTabs";
import {useNavigate, useParams} from "react-router-dom";
import {ParamBrandHouse} from "../../Parameters";
import * as React from "react";
import {useCallback} from "react";
import {TrackingViewList} from "../lists/TrackingViewList";
import {ConsumerEvents} from "./ConsumerEvents";
import {FlexCommand, FlexListColumn} from "../../components/flexlist/FlexTypes";
import {FlexList} from "../../components/flexlist/FlexList";
import {Alert, Box} from "@mui/material";
import {Mail} from "@mui/icons-material";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import dayjs from "dayjs";
import Button from "@mui/material/Button";
import {Dialogs} from "../../DialogProvider";
import {SavingUI} from "../../SavingProvider";
import {
   PlannedMail,
   SortFindManyPlannedMailInput,
   useBrandConsumersPageShopsQuery,
   useConsumerEventsTradeInQuery,
   useConsumerEventsVoucherQuery,
   useConsumerMailResendMutation,
   useConsumerPlannedMailsLazyQuery,
   useConsumerSendAllMutation
} from "../../Queries";
import {TradeInPagePath} from "../shop/tradeins/TradeInPage";
import {VoucherPagePath} from "./vouchers/VoucherPage";

export const BrandTrackingPage = () => {
   const {brandHouseId} = useParams<ParamBrandHouse>();
   const {data: shopsData} = useBrandConsumersPageShopsQuery({
      variables: {brandHouseId: brandHouseId!},
      skip: !brandHouseId
   });
   const shopIds = shopsData?.ShopMany?.map(s => s._id) || [];

   const tabs: PageTab[] = [{
      id: 'activity',
      label: 'Activity'
   }, {
      id: 'events',
      label: 'Shop events'
   }, {
      id: 'planned',
      label: 'Planned mails'
   }];

   return <Page>
      <PageContentTabs tabs={tabs} render={(tab) => {
         return <PageContent fullHeight>
            {tab === 'activity' && <TrackingViewList brandColumn={false} filter={{
               brandHouseId: brandHouseId
            }}/>}
            {tab === 'events' && <ConsumerEvents/>}
            {tab === 'planned' && <BrandPlannedMails shopIds={shopIds}/>}
         </PageContent>
      }}/>
   </Page>
}

const BrandPlannedMails = ({shopIds}: { shopIds: string[] }) => {
   const {brandHouseId} = useParams<ParamBrandHouse>();
   const [fetchMore] = useConsumerPlannedMailsLazyQuery();
   const [resend] = useConsumerMailResendMutation();
   const [sendAll] = useConsumerSendAllMutation();
   const navigate = useNavigate();

   const columns: FlexListColumn<any>[] = [{
      id: 'sendAt',
      field: 'sendAt',
      label: 'Planned for',
      render: (data) => {
         return dayjs(data).format('DD-MM-YYYY HH:mm')
      }
   }, {
      id: 'receiver',
      field: '_id',
      label: 'Receiver',
      render: (data, row) => {
         if (row.objectType === 'tradein') {
            return <TradeInChip tradeIn={row.objectId}/>
         } else {
            return <VoucherChip voucherId={row.objectId}/>
         }
      }
   }, {
      id: 'mailType',
      field: 'mailType',
      label: 'Mail',
      render: (data, row) => {
         return <Box sx={{display: 'flex', flexDirection: 'row', gap: '10px'}}>
            {row.objectType === 'voucher' && <CardGiftcardIcon/>}
            {row.objectType === 'tradein' && <DoubleArrowIcon/>}
            {row.objectType === 'voucher' && <>Voucher Usage Reminder</>}
            {row.objectType === 'tradein' && <>TradeIn Package Reminder</>}
         </Box>
      }
   }, {
      id: 'status',
      field: 'sent',
      label: 'Status',
      render: (data, row) => {
         if (row.sent) {
            return <Alert severity={"success"} title={dayjs(data).format('DD-MM-YYYY HH:mm:ss')}>Sent</Alert>
         } else {
            return <></>
         }
      }
   }];

   const onRequestData = useCallback(async (search: string | undefined, sort: string | undefined, page: number, filter: any) => {
      const shopFilter = shopIds.length > 0
         ? {OR: shopIds.map(id => ({shopId: id}))}
         : {};

      const {data} = await fetchMore({
         variables: {
            page: page,
            sort: (sort as SortFindManyPlannedMailInput) || SortFindManyPlannedMailInput.SHOPID__SENDAT_DESC,
            perPage: 20,
            search: search,
            filter: {
               ...shopFilter,
               ...filter
            }
         }
      });

      return {
         data: data?.PlannedMailsPagination?.items || [],
         totalCount: data?.PlannedMailsPagination?.count || 0
      }
   }, [fetchMore, shopIds]);

   const commands: FlexCommand<Partial<PlannedMail>>[] = [{
      id: 'resend',
      icon: Mail,
      title: 'Resend',
      execute: async (rows) => {
         for (const row of rows) {
            await resend({
               variables: {
                  mailId: row._id
               }
            })
         }
      }
   }];

   const onSelectRow = (row: any) => {
      if (row.objectType === 'tradein') {
         navigate(TradeInPagePath(brandHouseId!, row.shopId, row.objectId))
      } else {
         navigate(VoucherPagePath(brandHouseId!, row.shopId, row.objectId))
      }
   }

   const onSendVoucherReminders = async () => {
      Dialogs.confirm({
         title: 'Are you sure?',
         subtitle: 'This will send reminder mails to all voucher owners that have unused vouchers.'
      }, async () => {
         await SavingUI.process(async () => {
            for (const shopId of shopIds) {
               await sendAll({variables: {shopId}});
            }
         }, 'Sending voucher reminder mails')
      })
   }

   return <>
      <Box sx={{marginBottom: '10px', textAlign: 'right'}}>
         <Button onClick={onSendVoucherReminders}>Send voucher reminder mails</Button>
      </Box>
      <FlexList
         globalId={"brandPlannedMails"}
         selections={true}
         columns={columns}
         onRequestData={onRequestData}
         itemsPerPage={20}
         onSelectRow={onSelectRow}
         commands={commands}
      />
   </>
}

const TradeInChip = ({tradeIn}: { tradeIn: string }) => {
   const {data} = useConsumerEventsTradeInQuery({
      variables: {tradeInID: tradeIn}
   })
   return <Box>{data?.TradeIn?.consumer?.name}</Box>
}

const VoucherChip = ({voucherId}: { voucherId: string }) => {
   const {data} = useConsumerEventsVoucherQuery({
      variables: {voucherID: voucherId}
   })
   return <Box>{data?.VoucherById?.consumer?.name}</Box>
}
